import {
  EllipsisSpan,
  FormContent,
  Paging,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
  Button,
  Tabs,
  PopConfirm,
  Form,
  Select,
  Table,
  useUpdate,
  CustomFilter,
  Tag,
  Themes,
  useTheme,
  Modal,
} from '@maxtropy/components';
import { Space } from 'antd';
import { FC, useState, useEffect, useRef, useMemo } from 'react';
import styles from './index.module.scss';
import DeviceModal from './components/DeviceModal';
import AssociateTagModal from './components/AssociateTagModal';
import {
  apiV2ComprehensiveLabelPagePost,
  apiV2ComprehensiveLabelListPost,
  apiV2ComprehensiveLabelDeletePost,
} from '@maxtropy/device-customer-apis-v2';
import { CircuitLabelType, circuitLabelTypeDisplay } from '@/api/circuit';

import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import CircuitLabelModal from './CircuitLabelModal';
import CircuitLinkModal from './CircuitLinkModal';
import BindUnitModal from './BindUnitModal';
export enum TabKey {
  CIRCUIT = '0',
  ENERGY_UNIT = '1',
  ALARM_RULE = '2',
  DEVICE = '3',
}
interface SearchParams {
  ids?: number[];
}

type TabItem = { key: TabKey; label: React.ReactNode };

const CircuitLabels: FC = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const labelId = urlSearchParams.get('labelId');
  const routesContext = useBreadcrumbRoutes();

  const hasCircuitLabelTab = useHasPermission(PermissionsType.B_CIRCUIT_LABEL_TAB);
  const hasEnergyConsumingUnitTab = useHasPermission(PermissionsType.B_ENERGY_CONSUMING_UNIT_TAB);
  const hasAlarmLabelTab = useHasPermission(PermissionsType.B_ALARM_LABEL_TAB); // 报警
  const hasDeviceLabelTab = useHasPermission(PermissionsType.B_DEVICE_LABEL_Tab);

  const [searchForm] = Form.useForm(); // 搜索的表单

  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo; // 分页的一些配置

  const [showLabelModal, setShowLabelModal] = useState<boolean>(false);
  const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
  const [showEnergyUnitModal, setShowEnergyUnitModal] = useState<boolean>(false);

  const [opType, setOpType] = useState('add');
  const [currentLabelId, setCurrentLabelId] = useState<number | undefined>(undefined);
  const [currentLabelName, setCurrentLabelName] = useState('');
  const [currentLinkIds, setCurrentLinkIds] = useState([]);
  const [currentEnergyUnitIds, setCurrentEnergyUnitIds] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [tabKey, setTabKey] = useState<TabKey | undefined>(undefined);
  const [update] = useUpdate();
  const theme = useTheme();
  const initializedRef = useRef(false);
  const [associateDeviceModal, setAssociateDeviceModal] = useState<boolean>(false);
  const [associateAlarmRuleModal, setAssociateAlarmRuleModal] = useState<boolean>(false);
  const [currentAlarmRuleIds, setCurrentAlarmRuleIds] = useState<number[]>([]);
  const [currentDeviceIds, setCurrentDeviceIds] = useState<number[]>([]);
  const maybeItems: Array<TabItem | null> = [
    hasCircuitLabelTab ? { key: TabKey.CIRCUIT, label: '回路标签' } : null,
    hasEnergyConsumingUnitTab ? { key: TabKey.ENERGY_UNIT, label: '用能单元标签' } : null,
    hasAlarmLabelTab ? { key: TabKey.ALARM_RULE, label: '报警规则标签' } : null,
    hasDeviceLabelTab ? { key: TabKey.DEVICE, label: '设备标签' } : null,
  ];

  const items = useMemo(
    () => maybeItems.filter((i): i is TabItem => Boolean(i)),
    [hasCircuitLabelTab, hasEnergyConsumingUnitTab, hasAlarmLabelTab, hasDeviceLabelTab]
  );

  useEffect(() => {
    searchForm.setFieldValue('ids', undefined);
    setSearchParams({ ids: undefined });
    if (initializedRef.current) return;
    if (!items || items.length === 0) return;
    if (labelId) {
      const res = items.find(f => f.key === labelId);
      setTabKey(res?.key);
    } else {
      setTabKey(items[0].key);
    }
    initializedRef.current = true;
  }, [items, labelId, tabKey]);

  const {
    data: list,
    refresh,
    loading: listLoading,
  } = useRequest(
    () => {
      return apiV2ComprehensiveLabelPagePost({
        page: pageOffset,
        size: pageSize,
        type: tabKey,
        ...(searchParams ?? {}),
      }).then(res => {
        setTotalCount(res.total!);
        return res.list;
      });
    },
    {
      refreshDeps: [searchParams, pageOffset, pageSize, update, tabKey],
      ready: !!tabKey,
    }
  );

  const { data: labelList, refresh: labelListRefresh } = useRequest(
    () => apiV2ComprehensiveLabelListPost({ id: tabKey }),
    {
      refreshDeps: [tabKey],
      ready: !!tabKey,
    }
  );

  const columns = [
    {
      title: '标签名称',
      dataIndex: 'name',
      width: 160,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 160,
      ellipsis: { showTitle: true },
      render: (v: CircuitLabelType) => {
        return (
          <EllipsisSpan
            value={
              <Tag border="solid" color={v === CircuitLabelType.PLATFORM ? 'info' : 'success'}>
                {circuitLabelTypeDisplay[v]}
              </Tag>
            }
          />
        );
      },
    },
    {
      title: '关联用能单元数',
      dataIndex: 'relatedNum',
      width: 160,
      ellipsis: { showTitle: true },
      hidden: tabKey !== TabKey.ENERGY_UNIT,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '关联回路数',
      dataIndex: 'relatedNum',
      width: 160,
      ellipsis: { showTitle: true },
      hidden: tabKey !== TabKey.CIRCUIT,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '报警规则数',
      dataIndex: 'relatedNum',
      width: 160,
      ellipsis: { showTitle: true },
      hidden: tabKey !== TabKey.ALARM_RULE,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '设备数',
      dataIndex: 'relatedNum',
      width: 160,
      ellipsis: { showTitle: true },
      hidden: tabKey !== TabKey.DEVICE,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '最后操作人',
      dataIndex: 'staffName',
      width: 160,
      ellipsis: { showTitle: true },
      render: (v: string) => {
        return <EllipsisSpan value={v} />;
      },
    },
    {
      title: '最后操作时间',
      dataIndex: 'updateTime',
      width: 160,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
    },
  ];
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      fixed: 'right' as 'right',
      width: 260,
      render: (record: any) => (
        <Space size={16}>
          {tabKey === TabKey.CIRCUIT && (
            <Button
              type="link"
              onClick={() => {
                setCurrentLinkIds(record.objectIds);
                setCurrentLabelId(record.id);
                setShowLinkModal(true);
              }}
            >
              关联回路
            </Button>
          )}

          {tabKey === TabKey.ENERGY_UNIT && (
            <Button
              type="link"
              onClick={() => {
                setCurrentEnergyUnitIds(record.objectIds);
                setCurrentLabelId(record.id);
                setShowEnergyUnitModal(true);
              }}
            >
              关联用能单元
            </Button>
          )}
          {tabKey === TabKey.ALARM_RULE && (
            <Button
              type="link"
              disabled={record.type === CircuitLabelType.PLATFORM}
              onClick={() => {
                setCurrentAlarmRuleIds(record.objectIds);
                setCurrentLabelId(record.id);
                setCurrentLabelName(record.name);
                setAssociateAlarmRuleModal(true);
              }}
            >
              关联报警规则
            </Button>
          )}
          {tabKey === TabKey.DEVICE && (
            <Button
              type="link"
              disabled={record.type === CircuitLabelType.PLATFORM}
              onClick={() => {
                setCurrentDeviceIds(record.objectIds);
                setCurrentLabelId(record.id);
                setCurrentLabelName(record.name);
                setAssociateDeviceModal(true);
              }}
            >
              关联设备
            </Button>
          )}
          <Button
            type="link"
            disabled={record.type === CircuitLabelType.PLATFORM}
            onClick={() => {
              setOpType('edit');
              setCurrentLabelId(record.id);
              setCurrentLabelName(record.name);
              setShowLabelModal(true);
            }}
          >
            编辑标签
          </Button>

          <PopConfirm
            placement="bottomRight"
            title={'是否删除该标签？删除后无法恢复。'}
            okText="确认"
            cancelText="取消"
            onConfirm={() => onDeleteRecord(record.id)}
          >
            <Button disabled={record.type === CircuitLabelType.PLATFORM} type="link">
              删除
            </Button>
          </PopConfirm>
        </Space>
      ),
    },
  ];

  const { runAsync: deleteApi } = useRequest(params => apiV2ComprehensiveLabelDeletePost(params), {
    manual: true,
    refreshDeps: [currentLabelId],
  });
  // 表格删除按钮
  const onDeleteRecord = (id: number) => {
    deleteApi({ id }).then(() => {
      setPageOffset(1);
      refresh();
      labelListRefresh();
    });
  };

  return (
    <div className={styles.customFilterBox}>
      <Wrapper
        routes={routesContext?.routes}
        filters={
          <div>
            <Tabs
              activeKey={tabKey}
              items={items}
              selectedLinePlacement="top"
              onChange={(activeKey: string) => setTabKey(activeKey as TabKey)}
            />
            <div style={{ padding: '0 20px' }}>
              <CustomFilter
                form={searchForm}
                onFinish={(values: any) => {
                  setSearchParams(values);
                  setPageOffset(1);
                }}
                onReset={() => {
                  setSearchParams({ ids: undefined });
                  setPageOffset(1);
                }}
                collapseOpen={false}
              >
                <Form.Item name="ids" label="标签名称">
                  <Select
                    placeholder="请选择"
                    options={labelList?.list?.map(item => ({ value: item.id, label: item.name }))}
                    allowClear
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </CustomFilter>
            </div>
          </div>
        }
      >
        {hasCircuitLabelTab || hasAlarmLabelTab || hasDeviceLabelTab || hasEnergyConsumingUnitTab ? (
          <>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  setCurrentLabelId(undefined);
                  setOpType('add');
                  setShowLabelModal(true);
                }}
              >
                新建标签
              </Button>
            </Space>

            <FormContent style={{ padding: 0 }}>
              <Table
                rowKey="id"
                sticky
                scroll={{ x: 1300 }}
                loading={listLoading}
                columns={buildColumns}
                dataSource={list}
                pagination={false}
              />
              <Paging pagingInfo={pagingInfo} />
            </FormContent>
          </>
        ) : (
          <div className={styles.contentNot}>
            <div className={`${styles.logoNot} ${theme === Themes.DARK ? styles.darkImg : styles.lightImg}`} />
            <div>暂无权限</div>
          </div>
        )}
      </Wrapper>
      {/* 标签弹框 */}
      {showLabelModal && (
        <CircuitLabelModal
          type={tabKey}
          opType={opType}
          id={currentLabelId}
          name={currentLabelName}
          onCancel={() => setShowLabelModal(false)}
          onConfirm={() => {
            setShowLabelModal(false);
            setCurrentLabelId(undefined);
            setPageOffset(1);
            refresh();
            // 下拉列表刷新
            labelListRefresh();
          }}
        />
      )}
      {/* 关联回路弹框 */}
      {showLinkModal && (
        <CircuitLinkModal
          linkIds={currentLinkIds}
          labelId={currentLabelId}
          onCancel={() => setShowLinkModal(false)}
          onConfirm={() => {
            setShowLinkModal(false);
            setPageOffset(1);
            refresh();
          }}
        />
      )}
      {/* 关联用能单元弹框 */}
      {showEnergyUnitModal && (
        <BindUnitModal
          setOpen={setShowEnergyUnitModal}
          energyUnitIds={currentEnergyUnitIds}
          labelId={currentLabelId}
          updateFn={refresh}
          setPageOffset={setPageOffset}
        />
      )}

      {associateDeviceModal && (
        <DeviceModal
          linkIds={currentDeviceIds}
          labelId={currentLabelId}
          onChangeSelectKeys={keys => {
            // Handle selection change
          }}
          onConfirm={() => {
            setAssociateDeviceModal(false);
            setPageOffset(1);
            refresh();
          }}
          onCancel={() => {
            setAssociateDeviceModal(false);
          }}
        />
      )}

      {associateAlarmRuleModal && (
        <AssociateTagModal
          linkIds={currentAlarmRuleIds}
          labelId={currentLabelId}
          onChangeSelectKeys={keys => {
            // Handle selection change
          }}
          onConfirm={() => {
            setAssociateAlarmRuleModal(false);
            setPageOffset(1);
            refresh();
          }}
          onCancel={() => {
            setAssociateAlarmRuleModal(false);
          }}
        />
      )}
    </div>
  );
};

export default CircuitLabels;
